import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';

const SearchableBrandDropdown = ({ 
  brands, 
  value, 
  onChange, 
  placeholder = "Type to search brands...",
  label = "Brand Name",
  required = false,
  error = null 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBrands, setFilteredBrands] = useState(brands);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter brands based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredBrands(brands);
    } else {
      const filtered = brands.filter(brand =>
        brand.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        brand.code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    }
  }, [searchTerm, brands]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set search term when value changes externally
  useEffect(() => {
    if (value) {
      const selectedBrand = brands.find(brand => brand.value === value);
      if (selectedBrand) {
        setSearchTerm(selectedBrand.label);
      }
    } else {
      setSearchTerm('');
    }
  }, [value, brands]);

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    
    // If input is cleared, clear the selected value
    if (!newValue) {
      onChange(null);
    }
  };

  const handleBrandSelect = (brand) => {
    setSearchTerm(brand.label);
    onChange(brand);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredBrands.length === 1) {
        handleBrandSelect(filteredBrands[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (value) {
      const selectedBrand = brands.find(brand => brand.value === value);
      return selectedBrand ? selectedBrand.label : searchTerm;
    }
    return searchTerm;
  };

  return (
    <div className="space-y-2" ref={dropdownRef}>
      <Label htmlFor="brandSearch">
        {label} {required && '*'}
      </Label>
      
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={inputRef}
            type="text"
            value={getDisplayValue()}
            onChange={handleInputChange}
            onFocus={handleInputClick}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`pl-10 pr-10 ${error ? 'border-red-500' : ''}`}
          />
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {filteredBrands.length > 0 ? (
              <div className="py-1">
                {filteredBrands.map((brand) => (
                  <button
                    key={brand.value}
                    type="button"
                    onClick={() => handleBrandSelect(brand)}
                    className="w-full px-3 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{brand.label}</div>
                      <div className="text-sm text-gray-500">
                        {brand.code} • {brand.company}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? `No brands found for "${searchTerm}"` : 'No brands available'}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {brands.length === 0 && (
        <p className="text-sm text-gray-500">
          No active brands found. Please add brands in Brand Specifications first.
        </p>
      )}
    </div>
  );
};

export default SearchableBrandDropdown;